import { useMemo } from 'react'

import { IconCaretDown } from '@tabler/icons-react'
import { Menu } from 'antd'
import styled from 'styled-components'

import { BaseButton } from './BaseButton'

import { ButtonProps } from '@/components/shared-components/BUI/Button'
import { Dropdown } from '@/components/shared-components/BUI/Dropdown'
import { ProjectIcon } from '@/components/shared-components/ProjectIcon'
import { BUILDER_PROJECT_STATUS, useProject } from '@/context/projectContext'
import { deterministicThemeColour } from '@/utils/getColour'

import { ProjectStatus } from 'types/api.graphql'

const Row = styled.div`
  display: flex;
  align-items: center;
`

const Name = styled.span`
  margin-left: 1rem;
`

const BANNED = [ProjectStatus.Archived, BUILDER_PROJECT_STATUS]

const getIcon = (id: string) => {
  if (!id) return null
  return <ProjectIcon size={24} colour={deterministicThemeColour(id)} />
}

type Props = ButtonProps & {
  value?: string
  onChange?: (value: string) => void
  filterToStatus?: Array<ProjectStatus | typeof BUILDER_PROJECT_STATUS>
}

export const ProjectDropdown: React.FC<Props> = ({
  value,
  onChange,
  filterToStatus = [],
  ...buttonProps
}) => {
  const { projects } = useProject()

  const activeProjects = useMemo(() => {
    return projects.filter((p) => {
      const isStatusAllowed = !BANNED.includes(p.status)
      const hasNoFilterToStatus = filterToStatus.length === 0
      const isStatusIncludedInFilter = filterToStatus.includes(p.status)

      return (
        isStatusAllowed && (hasNoFilterToStatus || isStatusIncludedInFilter)
      )
    })
  }, [projects, filterToStatus])

  const menu = (
    <Menu
      style={{ maxHeight: '300px', overflow: 'auto' }}
      items={[
        ...activeProjects.map((project) => ({
          key: project.id,
          label: (
            <Row>
              {getIcon(project.id)}
              <Name>{project.name}</Name>
            </Row>
          ),
          onClick: () => onChange?.(project.id),
        })),
      ]}
    />
  )

  const selectedProject = activeProjects.find((p) => p.id === value)

  return (
    <Dropdown overlay={menu} placement="bottomLeft" trigger={['click']}>
      <BaseButton
        {...buttonProps}
        hasValue={!!value}
        icon={getIcon(selectedProject?.id)}
      >
        {selectedProject?.name || 'Select project'}
        <IconCaretDown size={14} fill="black" />
      </BaseButton>
    </Dropdown>
  )
}
